import { getCurrentInstance, Ref, ref, toRaw, unref, watch } from 'vue';
import {
    disabledProps,
    DisabledPropsInterface,
    useDisableComposable,
} from '@src/application/composables/disabled-composable.js';
import { ConvertUndefinedToNullType } from '@src/application/convert-undefined-to-null-type.js';
import { isArray } from 'lodash';

/**
 * @deprecated nicht-typescript Variante um defineProps erfüllen zu können
 */
export const modelValueProps = {
    emitInvalid: {
        type: Boolean,
        default: false,
    },
    modelValue: {
        default: null,
        validator: () => true,
    },
    ...disabledProps,
};

/**
 * @deprecated nicht-typescript Variante um defineEmits erfüllen zu können
 */
export const modelValueEmits = ['update:modelValue'];

/**
 * interface zur Nutzung in defineProps:
 * Als generic kann hier der gewünscht Typ der modelValue-Property angegeben werden, z.B.:
 * - defineProps<ModelValuePropsInterface<string>>() -> modelValueProperty vom Typ string - required;
 * - defineProps<ModelValuePropsInterface<string|null>>() -> modelValueProperty vom Typ string|null - ACHTUNG! required, trotz nullable;
 * - defineProps<ModelValuePropsInterface<string|undefined>>() -> modelValueProperty vom Typ string - nicht required, da auch undefined geht;
 *
 * withDefaults() braucht es nicht, da im model-value-composable die defaults gesetzt werden
 */
export interface ModelValuePropsInterface<modelValueType = any> extends DisabledPropsInterface {
    emitInvalid?: boolean|false,
    modelValue?: modelValueType,
}

/**
 * type zur Nutzung in defineEmits:
 * Als generic kann hier der gewünscht Typ der modelValue-Property angegeben werden
 * z.B. defineEmits<ModelValueEmitsType<string>>() -> dann emitiert das composable ein event update:modelValue mit dem typ string;
 *      defineEmits<ModelValueEmitsType<string|undefined>>() -> dann emitiert das composable ein event update:modelValue mit dem typ string|null;
 *                                                              undefined wird dabei in null überführt, da das composable die prop, sollte sie undefined sein, in null umwandelt
 */
export type ModelValueEmitsType<modelValueType = any> = (event: 'update:modelValue', modelValue: ConvertUndefinedToNullType<modelValueType>|null) => void;

/**
 * Rückgabe-Interface für composable
 * wird nur hier verwendet
 */
interface ModelValueComposableInterface<modelValueType = any> {
    exposes: {
        reset(): void,
    },
    modelValue: Ref<ConvertUndefinedToNullType<modelValueType>|null>
}

/**
 * Der Default <modelValueType = any> ist nur für Components in denen noch kein type-script implementiert ist.
 * Der modelValueType sollte aber im Fall von type-script in jedem Fall in die useModelValueComposable-Funktion eingegeben werden.
 * z.B. useModelValueComposable<string>()
 * */
export function useModelValueComposable<modelValueType = any>(validation = (): string|true => true): ModelValueComposableInterface<modelValueType> {
    const instance = getCurrentInstance();
    if (instance === null) {
        throw new Error('no valid component');
    }
    const props = instance.props as unknown as ModelValuePropsInterface<modelValueType>;
    const emit: ModelValueEmitsType<modelValueType> = instance.emit;

    const modelValue = ref(unref(props.modelValue ?? null)) as Ref<ConvertUndefinedToNullType<modelValueType>|null>;
    watch(() => props.modelValue, reset, { deep: true });

    const { disabled } = useDisableComposable();

    watch(modelValue, () => {
        if (disabled.value) {
            return;
        }

        // wenn Value der Property schon gesetzt ist, muss kein event emitiert werde
        if (!isArray(modelValue.value) && toRaw(modelValue.value) === toRaw(props.modelValue)) {
            return;
        }
        if (isArray(modelValue.value) && isArray(props.modelValue) && props.modelValue && modelValue.value.length === props.modelValue.length) {
            let modelValueChanged = false;
            for (const entry of modelValue.value) {
                if (props.modelValue.includes(entry)) {
                    continue;
                }
                modelValueChanged = true;
                break;
            }

            if (!modelValueChanged) {
                return;
            }
        }
        if (validation() !== true && !props.emitInvalid) {
            return;
        }
        emit('update:modelValue', modelValue.value);
    }, { deep: true });

    function reset() {
        if (props.modelValue === undefined) {
            modelValue.value = null;
            return;
        }
        modelValue.value = props.modelValue as unknown as ConvertUndefinedToNullType<modelValueType>;
    }

    return {
        modelValue,
        exposes: {
            reset,
        },
    };
}
